// === SAA:UI:APPLICANT-SUMMARY ===
// Single source for the applicant-data presentation: the personal label/value grid
// plus the grouped "Verzekerd …" highlight boxes (object / voertuig / personen) that
// adapt to the selected products. Shared attributes (gezinssamenstelling,
// verzekeringnemer) render ONCE in the Verzekerde-personen box, never per product.
// That box is about the PERSONS only (who is covered, beroepsmilitair for DFD);
// coverage facts (verzekerde bedragen, dekkingen, dekkingsgebied) stay out of it
// by customer decision (2026-08-21) — they live on the product screens.
// Rendered by the Offerte gegevens card and the confirmation "Controleer uw gegevens" card
// (identical: the insured amounts are not shown to the customer here either).

import React from 'react';
import { useTranslation } from 'react-i18next';
import { useWorkflow, useSharedAddress, useConfig } from '@sluitstraat/core';
import { workflowSummaryStyles as styles } from '../styles/commonStyles';
import { formatLicensePlate } from '../utils/licensePlate';
import { hiddenOngevallenFields } from '../pages/screens/ongevallen/fields';
import { GSS_CODE_TO_I18N_KEY } from '../utils/familyComposition';
import { houseTypeI18nKey } from '../utils/houseType';

export const ApplicantSummary: React.FC = () => {
  const { t } = useTranslation();
  const { data, selectedProducts } = useWorkflow();
  const { address: sharedBa } = useSharedAddress();
  const { config } = useConfig();
  const hiddenOngevallen = hiddenOngevallenFields(config);
  const clientInfo = data['client-information']?.clientInfo || {};

  return (
    <>
      {/* Personal info */}
      {(() => {
        const woonhuisData = data['woonhuis']?.Contractdocument?.PP?.BA;
        const inboedelData = data['inboedel']?.Contractdocument?.PP?.BA;
        const aansprakelijkheidData = data['aansprakelijkheid']?.Contractdocument?.PP?.BA;
        const personenautoData = data['personenauto']?.Contractdocument?.PP?.BA;
        const rechtsbijstandData = data['rechtsbijstand']?.Contractdocument?.PP?.BA;
        const doorlopendereisVP = data['doorlopendereis']?.Contractdocument?.PP?.VP || data['doorlopendereis']?.Relatiedocument?.VP;
        const ongevallenVP = data['ongevallen']?.Relatiedocument?.VP || data['ongevallen']?.Contractdocument?.PP?.VP;
        const vpData = ongevallenVP?.VP_PCODE ? ongevallenVP : doorlopendereisVP;
        const street = woonhuisData?.BA_STRAAT || inboedelData?.BA_STRAAT || aansprakelijkheidData?.BA_STRAAT || personenautoData?.BA_STRAAT || rechtsbijstandData?.BA_STRAAT || vpData?.VP_STRAAT || sharedBa?.BA_STRAAT;
        const houseNumber = woonhuisData?.BA_HUISNR || inboedelData?.BA_HUISNR || aansprakelijkheidData?.BA_HUISNR || personenautoData?.BA_HUISNR || rechtsbijstandData?.BA_HUISNR || vpData?.VP_HUISNR || sharedBa?.BA_HUISNR;
        const houseAddition = woonhuisData?.BA_TOEVOEG || inboedelData?.BA_TOEVOEG || aansprakelijkheidData?.BA_TOEVOEG || personenautoData?.BA_TOEVOEG || rechtsbijstandData?.BA_TOEVOEG || vpData?.VP_TOEVOEG || sharedBa?.BA_TOEVOEG || '';
        const postalCode = woonhuisData?.BA_PCODE || inboedelData?.BA_PCODE || aansprakelijkheidData?.BA_PCODE || personenautoData?.BA_PCODE || rechtsbijstandData?.BA_PCODE || vpData?.VP_PCODE || sharedBa?.BA_PCODE;
        const city = woonhuisData?.BA_PLAATS || inboedelData?.BA_PLAATS || aansprakelijkheidData?.BA_PLAATS || personenautoData?.BA_PLAATS || rechtsbijstandData?.BA_PLAATS || vpData?.VP_PLAATS || sharedBa?.BA_PLAATS;
        const hasAddress = (selectedProducts?.woonhuis || selectedProducts?.inboedel || selectedProducts?.aansprakelijkheid || selectedProducts?.personenauto || selectedProducts?.rechtsbijstand || selectedProducts?.ongevallen || selectedProducts?.doorlopendereis) && street && houseNumber && postalCode && city;

        // Postcode as "1234 AB" (digits, space, letters); city renders on its own line below.
        const rawPostalCode = String(postalCode || '').replace(/\s/g, '').toUpperCase();
        const formattedPostalCode = /^\d{4}[A-Z]{2}$/.test(rawPostalCode)
          ? `${rawPostalCode.slice(0, 4)} ${rawPostalCode.slice(4)}`
          : postalCode;

        return (
          <div className="summary-info-grid" style={styles.infoGrid}>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>{t('screens.offerSummary.name')}</span>
              <span style={styles.infoValue}>
                {[clientInfo.initials, clientInfo.affix, clientInfo.lastName].filter(Boolean).join(' ') || '-'}
              </span>
            </div>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>{t('form.dateOfBirth')}</span>
              <span style={styles.infoValue}>{clientInfo.dateOfBirth || '-'}</span>
            </div>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>{t('screens.offerSummary.email')}</span>
              <span style={styles.infoValue}>{clientInfo.email || '-'}</span>
            </div>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>{t('screens.offerSummary.phone')}</span>
              <span style={styles.infoValue}>{clientInfo.phone || '-'}</span>
            </div>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>{t('screens.offerSummary.address')}</span>
              {hasAddress ? (
                <div style={styles.infoValue}>
                  <div>{`${street} ${houseNumber}${houseAddition ? ` ${houseAddition}` : ''}`}</div>
                  <div>{formattedPostalCode}</div>
                  <div>{city}</div>
                </div>
              ) : (
                <span style={styles.infoValue}>-</span>
              )}
            </div>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>{t('screens.offerSummary.nationality')}</span>
              <span style={styles.infoValue}>
                {clientInfo.nationality ? (t(`form.nationalityOptions.${clientInfo.nationality}`) || clientInfo.nationality) : '-'}
              </span>
            </div>
          </div>
        );
      })()}

      <div style={styles.innerDivider}></div>

      {/* Insured property address - shown when woonhuis or inboedel is selected */}
      {(selectedProducts?.woonhuis || selectedProducts?.inboedel) && (() => {
        // Verzekerd Object card shows essentials only (adres + soort woning). Bouwjaar,
        // oppervlakte and herbouw-/inboedelwaarde live on the product pages, not here.
        const woonhuisBA = data['woonhuis']?.Contractdocument?.PP?.BA;
        const woonhuisVP = data['woonhuis']?.Contractdocument?.PP?.VP;
        const inboedelBA = data['inboedel']?.Contractdocument?.PP?.BA;

        const ba = woonhuisBA || inboedelBA;
        const vpStreet = woonhuisVP?.VP_STRAAT || ba?.BA_STRAAT;
        const vpHuisnr = woonhuisVP?.VP_HUISNR || ba?.BA_HUISNR;
        const vpToevoeg = woonhuisVP?.VP_TOEVOEG || ba?.BA_TOEVOEG || '';
        const vpPcode = woonhuisVP?.VP_PCODE || ba?.BA_PCODE;
        const vpPlaats = woonhuisVP?.VP_PLAATS || ba?.BA_PLAATS;
        const hasPropertyAddress = vpStreet && vpHuisnr && vpPcode && vpPlaats;

        // Postcode as "1234 AB" (digits, space, letters); city renders on its own line below.
        const rawPcode = String(vpPcode || '').replace(/\s/g, '').toUpperCase();
        const formattedPcode = /^\d{4}[A-Z]{2}$/.test(rawPcode)
          ? `${rawPcode.slice(0, 4)} ${rawPcode.slice(4)}`
          : vpPcode;

        const typeWon = woonhuisBA?.BA_TYPEWON || inboedelBA?.BA_TYPEWON;
        const houseTypeKey = houseTypeI18nKey(typeWon);
        const houseTypeLabel = houseTypeKey ? t(`screens.woonhuis.options.houseType.${houseTypeKey}`) : '-';

        if (!hasPropertyAddress) return null;

        return (
          <div style={styles.insuredObjectBlock}>
            <div style={styles.insuredObjectHeader}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--primary-color, #056666)" strokeWidth="2">
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                <polyline points="9 22 9 12 15 12 15 22"></polyline>
              </svg>
              <span style={styles.insuredObjectTitle}>{t('screens.offerSummary.insuredProperty')}</span>
            </div>
            <div style={styles.insuredObjectGrid}>
              <div>
                <span style={styles.insuredObjectLabel}>
                  {selectedProducts?.woonhuis ? t('screens.offerSummary.insuredPropertyAddress') : t('screens.offerSummary.insuredContentsAddress')}
                </span>
                <div style={styles.insuredObjectValue}>
                  <div>{`${vpStreet} ${vpHuisnr}${vpToevoeg ? ` ${vpToevoeg}` : ''}`}</div>
                  <div>{formattedPcode}</div>
                  <div>{vpPlaats}</div>
                </div>
              </div>
              <div>
                <span style={styles.insuredObjectLabel}>{t('screens.offerSummary.houseType')}</span>
                <span style={styles.insuredObjectValue}>{houseTypeLabel}</span>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Vehicle details — shown when personenauto is selected. Mirrors the insured-property
          block style so the page has a consistent "insured object" visual per product group. */}
      {selectedProducts?.personenauto && (() => {
        // Verzekerd Object card shows essentials only (kenteken + auto). Bouwjaar,
        // cataloguswaarde and jaarkilometrage live on the product page, not here.
        const ob = data['personenauto']?.Contractdocument?.PP?.OB;
        if (!ob?.OB_KENTEKE && !ob?.OB_MERK && !ob?.OB_TYPE) return null;

        const merkType = [ob?.OB_MERK, ob?.OB_TYPE].filter(Boolean).join(' ');

        return (
          <div style={styles.insuredObjectBlock}>
            <div style={styles.insuredObjectHeader}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--primary-color, #056666)" strokeWidth="2">
                <path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.5 2.8C1.4 11.3 1 12.1 1 13v3c0 .6.4 1 1 1h2"></path>
                <circle cx="7" cy="17" r="2"></circle>
                <circle cx="17" cy="17" r="2"></circle>
              </svg>
              <span style={styles.insuredObjectTitle}>
                {t('screens.offerSummary.insuredVehicle', { defaultValue: 'Verzekerd voertuig' })}
              </span>
            </div>
            <div style={styles.insuredObjectGrid}>
              {ob?.OB_KENTEKE && (
                <div>
                  <span style={styles.insuredObjectLabel}>
                    {t('screens.offerSummary.licensePlate', { defaultValue: 'Kenteken' })}
                  </span>
                  <span style={styles.insuredObjectValue}>{formatLicensePlate(ob.OB_KENTEKE)}</span>
                </div>
              )}
              {merkType && (
                <div>
                  <span style={styles.insuredObjectLabel}>
                    {t('screens.offerSummary.vehicle', { defaultValue: 'Voertuig' })}
                  </span>
                  <span style={styles.insuredObjectValue}>{merkType}</span>
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* Insured persons — shown when a person-related coverage (aansprakelijkheid,
          rechtsbijstand, doorlopende reis, ongevallen) is selected. Those products don't
          have a physical insured object, so we surface "who is covered" via the
          gezinssamenstelling (GSS code: AVP on DA_GSS with VP_GSSC fallback,
          Rechtsbijstand on WC_GSS, Doorlopende reis on DR_GSS, Ongevallen on OD_GSS).
          Person facts only: no verzekerde bedragen / dekkingen / dekkingsgebied here. */}
      {(selectedProducts?.aansprakelijkheid || selectedProducts?.rechtsbijstand || selectedProducts?.ongevallen || selectedProducts?.doorlopendereis) && (() => {
        const avpPP = data['aansprakelijkheid']?.Contractdocument?.PP;
        const rbPP = data['rechtsbijstand']?.Contractdocument?.PP;
        const drPP = data['doorlopendereis']?.Contractdocument?.PP;
        const ogPP = data['ongevallen']?.Contractdocument?.PP;
        const gssCode =
          avpPP?.DA?.DA_GSS ||
          avpPP?.VP?.VP_GSSC ||
          (Array.isArray(rbPP?.WC) ? rbPP.WC[0]?.WC_GSS : rbPP?.WC?.WC_GSS) ||
          ogPP?.OD?.OD_GSS ||
          drPP?.DR?.DR_GSS;
        const gssKey = gssCode ? GSS_CODE_TO_I18N_KEY[String(gssCode)] : undefined;
        const gssLabel = gssKey ? t(gssKey) : undefined;

        const fullName = [clientInfo.initials, clientInfo.affix, clientInfo.lastName].filter(Boolean).join(' ');

        // Ongevallen — the beroepsmilitair answers (a fact about the insured person; the partner
        // variant is only asked — and only sent — for a gezinssamenstelling with partner).
        const yesNo = (v: unknown) => (String(v) === 'J' ? t('common.yes', 'Ja') : t('common.no', 'Nee'));
        const ogBermil = selectedProducts?.ongevallen && !hiddenOngevallen.has('VZ_BERMIL') && ogPP?.VZ?.VZ_BERMIL != null ? yesNo(ogPP.VZ.VZ_BERMIL) : null;
        const ogBermilPartner = selectedProducts?.ongevallen && !hiddenOngevallen.has('VZ_BERMILP') && ogPP?.VZ?.VZ_BERMILP != null ? yesNo(ogPP.VZ.VZ_BERMILP) : null;

        if (!gssLabel && !fullName && !ogBermil && !ogBermilPartner) return null;

        return (
          <div style={styles.insuredObjectBlock}>
            <div style={styles.insuredObjectHeader}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--primary-color, #056666)" strokeWidth="2">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                <circle cx="12" cy="7" r="4"></circle>
              </svg>
              <span style={styles.insuredObjectTitle}>
                {t('screens.offerSummary.insuredPersons', { defaultValue: 'Verzekerde personen' })}
              </span>
            </div>
            <div style={styles.insuredObjectGrid}>
              {fullName && (
                <div>
                  <span style={styles.insuredObjectLabel}>{t('screens.offerSummary.policyholder', { defaultValue: 'Verzekeringnemer' })}</span>
                  <span style={styles.insuredObjectValue}>{fullName}</span>
                </div>
              )}
              {gssLabel && (
                <div>
                  <span style={styles.insuredObjectLabel}>{t('screens.offerSummary.householdComposition', { defaultValue: 'Gezinssamenstelling' })}</span>
                  <span style={styles.insuredObjectValue}>{gssLabel}</span>
                </div>
              )}
              {ogBermil && (
                <div>
                  <span style={styles.insuredObjectLabel}>{t('screens.ongevallen.fields.bermil', 'Beroepsmilitair')}</span>
                  <span style={styles.insuredObjectValue}>{ogBermil}</span>
                </div>
              )}
              {ogBermilPartner && (
                <div>
                  <span style={styles.insuredObjectLabel}>{t('screens.ongevallen.fields.bermilp', 'Partner beroepsmilitair')}</span>
                  <span style={styles.insuredObjectValue}>{ogBermilPartner}</span>
                </div>
              )}
            </div>
          </div>
        );
      })()}

    </>
  );
};
